/**
 * requirements.js — THE single source of truth for per-case "requirements".
 *
 * A requirement is an admin-created, per-case ask for a document/evidence,
 * surfaced on the case-detail Notes panel. It is satisfied by UPLOADING a file
 * (no open/done flag is persisted — satisfaction is derived from the presence of
 * a non-deleted attachment whose category === REQ_CATEGORY_PREFIX + id).
 *
 * Persistence: requirements are stored as a JSON ARRAY under the `requirements`
 * key of the existing cases.type_specific NVARCHAR(MAX) column (ISJSON-guarded).
 * NO new table, NO migration. Near-Miss and other type_specific fields live in
 * the SAME object, so every merge here is non-destructive.
 *
 * Shared by BOTH runtimes:
 *   - the Azure Functions API (postCaseRequirement / deleteCaseRequirement),
 *     imported via the plain relative path within api/src.
 *   - the Vite frontend, imported through the `@shared` alias (= api/src/lib).
 *
 * HARD RULE: runtime-agnostic — no mssql / @azure / React / fs / path imports.
 * Pure data + pure functions only.
 */

// Attachment.category value that binds an uploaded file to a requirement.
// attachments.category is NVARCHAR(60); a requirement id is bounded to <= 50
// chars (see REQ_ID_MAX) so the prefixed value always fits.
export const REQ_CATEGORY_PREFIX = 'req:';

export const REQ_ID_MAX    = 50;   // keep 'req:' + id <= 60 (attachments.category)
export const REQ_LABEL_MAX = 120;  // matches the governed config label bound
export const REQ_NOTE_MAX  = 500;
export const REQ_SIGNOFF_MESSAGE_MAX = 500;

// A requirement is one of two KINDS:
//   'document' — satisfied by uploading a 'req:<id>' attachment (DERIVED, no flag)
//   'signoff'  — a task to be signed off; satisfaction is a STORED completion
//                object (there is no upload). Defaults to 'document' for back-compat
//                with requirements minted before the two-kind split.
export const REQ_KIND_DOCUMENT = 'document';
export const REQ_KIND_SIGNOFF  = 'signoff';
export const REQ_KINDS = [REQ_KIND_DOCUMENT, REQ_KIND_SIGNOFF];

/** Normalise a stored/legacy requirement's kind. Missing → 'document'. */
export function requirementKind(r) {
  return r && r.kind === REQ_KIND_SIGNOFF ? REQ_KIND_SIGNOFF : REQ_KIND_DOCUMENT;
}

/**
 * Resolve a CREATE request's `kind` strictly.
 *   - missing / null / '' → 'document' (back-compat with pre-split callers)
 *   - 'document' / 'signoff' → that kind
 *   - any OTHER explicit value (e.g. 'banana') → error (the caller 400s).
 * This differs from requirementKind() which is for READING stored/legacy data
 * (where an unknown value falls back silently). On WRITE we never silently
 * coerce an explicit unknown into a document.
 * @returns {{ ok: true, kind: string } | { ok: false, error: string }}
 */
export function resolveRequirementKind(rawKind) {
  if (rawKind == null || rawKind === '') return { ok: true, kind: REQ_KIND_DOCUMENT };
  if (REQ_KINDS.includes(rawKind)) return { ok: true, kind: rawKind };
  return { ok: false, error: `Unknown requirement kind "${rawKind}".` };
}

/** True when a sign-off requirement has a stored completion. */
export function isSignoffDone(r) {
  return requirementKind(r) === REQ_KIND_SIGNOFF && !!(r && r.completion && r.completion.done);
}

// The type_specific key the requirements array lives under.
export const REQ_TYPE_SPECIFIC_KEY = 'requirements';

// Starter defaults — MIRROR of AppConfigContext DEFAULT_CONFIG.requirementTypes.
// The server validates an add against the live config-sourced list when present,
// falling back to this mirror so an add is never trusted from a raw client key.
export const DEFAULT_REQUIREMENT_TYPES = [
  { key: 'consent-form',         label: 'Consent form' },
  { key: 'treatment-record',     label: 'Treatment record' },
  { key: 'photograph',           label: 'Photograph' },
  { key: 'incident-statement',   label: 'Incident statement' },
  { key: 'corrective-evidence',  label: 'Corrective-action evidence' },
  { key: 'training-certificate', label: 'Training certificate' },
  { key: 'post-photo',           label: 'Post photo' },
];

// The sentinel key that means "free-text label" — the picker always appends an
// "Other (specify)" option that reveals a label input.
export const REQ_OTHER_KEY = 'other';

const isBlank = (v) => v == null || String(v).trim() === '';

/** category string for a requirement id. */
export function reqCategory(id) {
  return `${REQ_CATEGORY_PREFIX}${id}`;
}

/** true if a raw attachment category binds to the given requirement id. */
export function categoryIsForRequirement(category, id) {
  return typeof category === 'string' && category === reqCategory(id);
}

/** Parse the requirement id out of a 'req:<id>' category (null if not a req). */
export function requirementIdFromCategory(category) {
  if (typeof category !== 'string' || !category.startsWith(REQ_CATEGORY_PREFIX)) return null;
  const id = category.slice(REQ_CATEGORY_PREFIX.length);
  return id || null;
}

/**
 * Read the requirements array out of a (parsed) type_specific object.
 * Tolerates null / non-object / missing key — always returns an array.
 */
export function getRequirements(typeSpecific) {
  if (!typeSpecific || typeof typeSpecific !== 'object') return [];
  const list = typeSpecific[REQ_TYPE_SPECIFIC_KEY];
  return Array.isArray(list) ? list : [];
}

/**
 * Mint a SHORT, stable, collision-resistant requirement id (<= REQ_ID_MAX).
 * Server-side only — callers pass an `existing` array to guarantee uniqueness.
 */
export function mintRequirementId(existing = [], rand = Math.random) {
  const taken = new Set((existing || []).map(r => r && r.id).filter(Boolean));
  // base36 timestamp + a little randomness keeps it short and sortable.
  for (let attempt = 0; attempt < 50; attempt++) {
    const t = Date.now().toString(36);
    const r = Math.floor(rand() * 1e6).toString(36);
    const id = `r-${t}-${r}`.slice(0, REQ_ID_MAX);
    if (!taken.has(id)) return id;
  }
  // Extremely unlikely fallback — still bounded.
  return `r-${Date.now().toString(36)}-${Math.floor(rand() * 1e9).toString(36)}`.slice(0, REQ_ID_MAX);
}

/**
 * Resolve + validate an add request against the allowlist.
 * @param {{ key?, label?, note? }} body
 * @param {Array<{key,label}>} allowList  config-sourced or mirrored default
 * @returns {{ ok: true, label: string, note: string|null, sourceKey: string }
 *          | { ok: false, error: string }}
 */
export function resolveRequirementAdd(body, allowList = DEFAULT_REQUIREMENT_TYPES) {
  const key = (body && body.key != null) ? String(body.key).trim() : '';
  if (!key) return { ok: false, error: 'A requirement type "key" is required.' };

  let label;
  if (key === REQ_OTHER_KEY) {
    // Free-text label REQUIRED for "other".
    if (isBlank(body && body.label)) {
      return { ok: false, error: 'A label is required when adding an "other" requirement.' };
    }
    label = String(body.label).trim();
  } else {
    // Must match the allowlist (config-sourced or mirrored default) — never trust
    // a raw client key.
    const match = (allowList || []).find(t => t && t.key === key);
    if (!match) {
      return { ok: false, error: `Unknown requirement type "${key}".` };
    }
    // The label comes from the allowlist, NOT the client (defence-in-depth).
    label = String(match.label || '').trim();
    if (!label) return { ok: false, error: `Requirement type "${key}" has no label.` };
  }

  if (label.length > REQ_LABEL_MAX) {
    return { ok: false, error: `Label is too long (max ${REQ_LABEL_MAX}).` };
  }

  let note = null;
  if (!isBlank(body && body.note)) {
    note = String(body.note).trim();
    if (note.length > REQ_NOTE_MAX) {
      return { ok: false, error: `Note is too long (max ${REQ_NOTE_MAX}).` };
    }
  }

  return { ok: true, label, note, sourceKey: key };
}

/**
 * Resolve + validate a SIGN-OFF add request. Unlike a document, a sign-off has
 * no allowlist — it is a bounded free-text task label the manager types. The
 * note is optional + bounded, identical to a document's note.
 * @param {{ label?, note? }} body
 * @returns {{ ok: true, label: string, note: string|null }
 *          | { ok: false, error: string }}
 */
export function resolveSignoffAdd(body) {
  if (isBlank(body && body.label)) {
    return { ok: false, error: 'A task to sign off is required.' };
  }
  const label = String(body.label).trim();
  if (label.length > REQ_LABEL_MAX) {
    return { ok: false, error: `Label is too long (max ${REQ_LABEL_MAX}).` };
  }

  let note = null;
  if (!isBlank(body && body.note)) {
    note = String(body.note).trim();
    if (note.length > REQ_NOTE_MAX) {
      return { ok: false, error: `Note is too long (max ${REQ_NOTE_MAX}).` };
    }
  }

  return { ok: true, label, note };
}

/**
 * Validate the (optional) message body on a sign-off completion. Bounded; never
 * required. Returns the trimmed message or null.
 * @returns {{ ok: true, message: string|null } | { ok: false, error: string }}
 */
export function resolveSignoffComplete(body) {
  if (isBlank(body && body.message)) return { ok: true, message: null };
  const message = String(body.message).trim();
  if (message.length > REQ_SIGNOFF_MESSAGE_MAX) {
    return { ok: false, error: `Message is too long (max ${REQ_SIGNOFF_MESSAGE_MAX}).` };
  }
  return { ok: true, message };
}

/**
 * Apply a sign-off completion to the matching requirement in a list, returning a
 * NEW list (the input list is not mutated). Pure — the caller decides what to do
 * with the {ok}/{error} result. A completion can only be applied ONCE, and only
 * to a 'signoff' requirement.
 * The completion timestamp is the SINGLE SOURCE OF TRUTH: it is minted HERE and
 * returned on requirement.completion.completedAt. The caller MUST read it back
 * for the audit event + the issuer email rather than minting its own — that way
 * the stored value, the case_event, and the email "on {date}" can never diverge.
 * Any `completedAt` on the `completion` arg is ignored on purpose.
 *
 * @returns {{ ok: true, requirements: Array, requirement: object }
 *          | { ok: false, status: number, error: string }}
 */
export function applySignoffCompletion(requirements, reqId, completion) {
  const list = Array.isArray(requirements) ? requirements : [];
  const target = list.find(r => r && r.id === reqId);
  if (!target) return { ok: false, status: 404, error: `Requirement "${reqId}" not found on this case.` };
  if (requirementKind(target) !== REQ_KIND_SIGNOFF) {
    return { ok: false, status: 400, error: 'Only a sign-off requirement can be completed.' };
  }
  if (isSignoffDone(target)) {
    return { ok: false, status: 400, error: 'This sign-off is already completed.' };
  }
  const completed = {
    ...target,
    completion: {
      done:           true,
      completedById:   completion.completedById   || null,
      completedByName: completion.completedByName || null,
      // SINGLE SOURCE: minted here, never taken from the caller.
      completedAt:     new Date().toISOString(),
      message:         completion.message != null ? completion.message : null,
    },
  };
  const next = list.map(r => (r && r.id === reqId ? completed : r));
  return { ok: true, requirements: next, requirement: completed };
}

/**
 * Merge requirements back into a parsed type_specific object WITHOUT clobbering
 * any other keys (Near-Miss fields live in the same bag). Returns a NEW object.
 */
export function mergeRequirements(typeSpecific, requirements) {
  const base = (typeSpecific && typeof typeSpecific === 'object' && !Array.isArray(typeSpecific))
    ? { ...typeSpecific }
    : {};
  base[REQ_TYPE_SPECIFIC_KEY] = Array.isArray(requirements) ? requirements : [];
  return base;
}

/** Safe parse of the type_specific JSON string column. */
export function parseTypeSpecific(s) {
  if (!s) return null;
  try { return JSON.parse(s); } catch { return null; }
}

// ── Requirement WAIVERS (required_evidence_logic.md §3) ──────────────────────
//
// A waiver records that a matrix-required document does not exist, with a
// reason: `type_specific.waivers = { [category]: { reason, byName, byEmail, at } }`.
// A waived slot counts as satisfied everywhere (case-file slot, wizard panel,
// server outstanding helper). Only `consent_form` is waivable in this release.
//
// Read-compat: PR #65's bare `type_specific.no_consent_form === true` is treated
// as a consent_form waiver with reason 'Declared at submission' — by getWaivers
// here AND by the frontend getWaivers mirror. Writers drop the legacy flag
// (postCase converts it; applyWaiver / removeWaiver delete it).

export const WAIVERS_KEY = 'waivers';
export const WAIVABLE_CATEGORIES = Object.freeze(['consent_form']);
export const WAIVER_REASON_MAX = 500;
export const LEGACY_NO_CONSENT_FORM_KEY = 'no_consent_form';
export const LEGACY_WAIVER_REASON = 'Declared at submission';

/** Human label for a waivable category (event labels / error messages). */
export const WAIVABLE_CATEGORY_LABELS = Object.freeze({
  consent_form: 'Consent form',
});

/** True when the category may carry a waiver. */
export function isWaivableCategory(category) {
  return typeof category === 'string' && WAIVABLE_CATEGORIES.includes(category);
}

/** Label for a waivable category — falls back to a humanised key. */
export function waivableCategoryLabel(category) {
  if (WAIVABLE_CATEGORY_LABELS[category]) return WAIVABLE_CATEGORY_LABELS[category];
  const s = String(category || '').replace(/_/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Document';
}

const isPlainObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);

/**
 * Read the waivers map out of a (parsed) type_specific object, converting the
 * legacy `no_consent_form: true` flag into a consent_form waiver when no
 * explicit waiver for it exists. Tolerates null / non-object / missing key —
 * always returns a plain object (possibly empty). Entries that are not objects
 * are ignored.
 * @returns {Record<string, { reason: string, byName: string|null, byEmail: string|null, at: string|null }>}
 */
export function getWaivers(typeSpecific) {
  const out = {};
  if (!isPlainObject(typeSpecific)) return out;
  const stored = typeSpecific[WAIVERS_KEY];
  if (isPlainObject(stored)) {
    for (const [category, w] of Object.entries(stored)) {
      if (!isPlainObject(w)) continue;
      out[category] = {
        reason:  w.reason  != null ? String(w.reason) : '',
        byName:  w.byName  != null ? String(w.byName)  : null,
        byEmail: w.byEmail != null ? String(w.byEmail) : null,
        at:      w.at      != null ? String(w.at)      : null,
      };
    }
  }
  if (typeSpecific[LEGACY_NO_CONSENT_FORM_KEY] === true && !out.consent_form) {
    out.consent_form = { reason: LEGACY_WAIVER_REASON, byName: null, byEmail: null, at: null };
  }
  return out;
}

/** True when the category is currently waived (explicit or legacy flag). */
export function hasWaiver(typeSpecific, category) {
  return Object.prototype.hasOwnProperty.call(getWaivers(typeSpecific), category);
}

/**
 * Validate a waiver reason: required, free text, trimmed, ≤ WAIVER_REASON_MAX.
 * @returns {{ ok: true, reason: string } | { ok: false, error: string }}
 */
export function validateWaiverReason(reason) {
  if (isBlank(reason)) return { ok: false, error: 'A reason is required to record that there is no document.' };
  const text = String(reason).trim();
  if (text.length > WAIVER_REASON_MAX) {
    return { ok: false, error: `Reason is too long (max ${WAIVER_REASON_MAX}).` };
  }
  return { ok: true, reason: text };
}

/**
 * Apply a waiver to a parsed type_specific object, returning a NEW object (the
 * input is not mutated; other keys — Near-Miss fields, requirements — survive).
 * The caller stamps byName/byEmail/at from the authenticated caller — never from a client
 * payload. Drops the legacy no_consent_form flag when waiving consent_form.
 * Pure: category/reason validation is the caller's job (isWaivableCategory /
 * validateWaiverReason).
 */
export function applyWaiver(typeSpecific, category, { reason, byName = null, byEmail = null, at = null } = {}) {
  const base = isPlainObject(typeSpecific) ? { ...typeSpecific } : {};
  const waivers = isPlainObject(base[WAIVERS_KEY]) ? { ...base[WAIVERS_KEY] } : {};
  waivers[category] = {
    reason:  String(reason ?? '').trim(),
    byName:  byName  || null,
    byEmail: byEmail || null,
    at:      at || new Date().toISOString(),
  };
  base[WAIVERS_KEY] = waivers;
  if (category === 'consent_form') delete base[LEGACY_NO_CONSENT_FORM_KEY];
  return base;
}

/**
 * Remove a waiver, returning a NEW type_specific object. Also drops the legacy
 * no_consent_form flag for consent_form, and the `waivers` key itself when the
 * map becomes empty (so an empty bag stays empty). Pure; no-op when not waived.
 */
export function removeWaiver(typeSpecific, category) {
  const base = isPlainObject(typeSpecific) ? { ...typeSpecific } : {};
  if (isPlainObject(base[WAIVERS_KEY])) {
    const waivers = { ...base[WAIVERS_KEY] };
    delete waivers[category];
    if (Object.keys(waivers).length) base[WAIVERS_KEY] = waivers;
    else delete base[WAIVERS_KEY];
  }
  if (category === 'consent_form') delete base[LEGACY_NO_CONSENT_FORM_KEY];
  return base;
}

// ── Display helpers (pure; shared by the SPA) ────────────────────────────────

/**
 * The set of category strings ('req:<id>') for the LIVE requirements on a case.
 * An uploaded attachment whose category is in this set belongs to a requirement
 * slot and must NOT be surfaced again in the generic Attachments grid.
 */
export function liveRequirementCategorySet(requirements) {
  const set = new Set();
  for (const r of (Array.isArray(requirements) ? requirements : [])) {
    if (r && r.id != null) set.add(reqCategory(r.id));
  }
  return set;
}

/**
 * True when an attachment is bound to a LIVE requirement (and so is rendered in
 * that requirement's slot). A 'req:<id>' attachment whose requirement was
 * removed is NOT bound to a live requirement — it falls back to the generic
 * Attachments grid so the orphaned file is never hidden.
 *
 * @param {{category?: string}} att
 * @param {Set<string>} liveCategorySet  result of liveRequirementCategorySet
 */
export function isBoundToLiveRequirement(att, liveCategorySet) {
  if (!att || !att.category) return false;
  return liveCategorySet instanceof Set && liveCategorySet.has(att.category);
}

/**
 * The displayable "extra" entries of a parsed type_specific object: every
 * key/value EXCEPT the requirements array (which has its own UI) and any caller-
 * supplied reserved keys (e.g. the Near-Miss structured fields). Guarantees the
 * requirements array is NEVER surfaced as a generic [object Object] extra.
 *
 * @param {object|null} typeSpecific
 * @param {Iterable<string>} reservedKeys
 * @returns {Array<[string, any]>}
 */
export function typeSpecificExtras(typeSpecific, reservedKeys = []) {
  if (!typeSpecific || typeof typeSpecific !== 'object' || Array.isArray(typeSpecific)) return [];
  const reserved = new Set([REQ_TYPE_SPECIFIC_KEY, ...reservedKeys]);
  return Object.entries(typeSpecific)
    .filter(([k, v]) => v != null && v !== '' && !reserved.has(k));
}

// ── Canonical attachment categories behind per-case requirements ─────────────
//
// The matrix slots (consent_form, training_certificate, post_photo, …) drive
// the two-step metadata uploads (uploadMetadata.js / SlotUploadModal) and the
// consent auto-withdraw. A per-case requirement is stored with a governed
// requirementTypes `key` (or 'other' + a free-text label) and satisfied by a
// 'req:<id>' upload, so on its own it carries NO canonical category. This
// mapper resolves one so a per-case "Training certificate" behaves like the
// training_certificate slot everywhere (metadata prompt + server validation,
// consent withdraw, outstanding-rule key):
//   1. by `key` through REQUIREMENT_KEY_CATEGORY_ALIASES (consent-form →
//      consent_form, …); the default keys with no slot equivalent
//      (treatment-record, incident-statement, corrective-evidence) → null;
//   2. otherwise ('other', or an admin-minted config key such as
//      'req-1725000000') by the LABEL, normalised (lowercase, runs of
//      non-alphanumerics → '_', trimmed) and matched against a canonical key or
//      a canonical label normalised the same way — so "Training certificate",
//      "training-certificate" and "Post photo" resolve but "training cert" does
//      NOT (no fuzzy matching: a wrong guess would demand metadata the uploader
//      cannot give).
// Stored attachment categories stay 'req:<id>' — the canonical category is a
// derived view, never persisted.

/** Canonical upload categories with their picker labels (mirror of the
 *  frontend ATTACHMENT_CATEGORIES minus the free 'other'). */
export const CANONICAL_ATTACHMENT_CATEGORY_LABELS = Object.freeze({
  photo:                          'Photos',
  training_certificate:           'Training certificate',
  consent_form:                   'Consent form',
  treating_therapist_statement:   'Treating therapist statement',
  pre_photo:                      'Pre photo',
  post_photo:                     'Post photo',
  dosage_chart:                   'Dosage chart',
  laser_log_files:                'Laser log files',
  injector_consultancy_agreement: 'Injector consultancy agreement',
  injector_insurance_policy:      'Injector insurance policy',
});

/** requirementTypes key (slug) → canonical attachment category. */
export const REQUIREMENT_KEY_CATEGORY_ALIASES = Object.freeze({
  'consent-form':                   'consent_form',
  'photograph':                     'photo',
  'training-certificate':           'training_certificate',
  'post-photo':                     'post_photo',
  'pre-photo':                      'pre_photo',
  'dosage-chart':                   'dosage_chart',
  'laser-log-files':                'laser_log_files',
  'treating-therapist-statement':   'treating_therapist_statement',
  'injector-consultancy-agreement': 'injector_consultancy_agreement',
  'injector-insurance-policy':      'injector_insurance_policy',
});

// Default requirement types that deliberately have NO slot equivalent — the
// label is never consulted for these.
const NO_CATEGORY_REQUIREMENT_KEYS = new Set(['treatment-record', 'incident-statement', 'corrective-evidence']);

/** 'Training certificate' → 'training_certificate'; '  Post-Photo!' → 'post_photo'. */
export function normaliseCategoryText(s) {
  return String(s == null ? '' : s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

const NORMALISED_LABEL_TO_CATEGORY = (() => {
  const map = {};
  for (const [key, label] of Object.entries(CANONICAL_ATTACHMENT_CATEGORY_LABELS)) {
    map[normaliseCategoryText(label)] = key;
  }
  map.photo = 'photo';   // singular "Photo" (the slot's label until July 2026)
  return Object.freeze(map);
})();

/**
 * Canonical attachment category for a per-case requirement, or null when it
 * has none (a sign-off, a default type with no slot equivalent, an unmatched
 * free-text label, junk input).
 * @param {{ key?: string, label?: string, kind?: string }|null} req
 * @returns {string|null}
 */
export function canonicalCategoryForRequirement(req) {
  if (!req || typeof req !== 'object') return null;
  if (requirementKind(req) === REQ_KIND_SIGNOFF) return null;
  const key = req.key != null ? String(req.key).trim() : '';
  if (key && key !== REQ_OTHER_KEY) {
    if (Object.prototype.hasOwnProperty.call(REQUIREMENT_KEY_CATEGORY_ALIASES, key)) {
      return REQUIREMENT_KEY_CATEGORY_ALIASES[key];
    }
    if (NO_CATEGORY_REQUIREMENT_KEYS.has(key)) return null;
  }
  const n = normaliseCategoryText(req.label);
  if (!n) return null;
  if (Object.prototype.hasOwnProperty.call(CANONICAL_ATTACHMENT_CATEGORY_LABELS, n)) return n;
  return NORMALISED_LABEL_TO_CATEGORY[n] || null;
}

/**
 * The category an upload should be VALIDATED as. A 'req:<id>' category
 * resolves to the canonical category of that LIVE requirement (null when the
 * requirement is gone or has no slot equivalent — metadata is then refused);
 * every other value (a matrix category, 'other', null) is returned unchanged.
 * @param {string|null} category      the multipart `category` field
 * @param {Array} requirements        the case's live requirements (getRequirements)
 * @returns {string|null}
 */
export function resolveUploadCategory(category, requirements) {
  const reqId = requirementIdFromCategory(category);
  if (!reqId) return category;
  const list = Array.isArray(requirements) ? requirements : [];
  const req = list.find(r => r && r.id != null && String(r.id) === reqId);
  return req ? canonicalCategoryForRequirement(req) : null;
}
